import { useParams } from 'react-router-dom';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { useRedirectIfNotAuthorized } from '@backrs_ui/grow-ui/src/hooks/useRedirectIfNotAuthorized';
import {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { ProfilePage } from './ProfilePage';
import { PublicProfile } from './PublicProfile';
import { ProfileValue } from './types/profile.types';
import {
  isAcademicMetricField,
  isObjectValue,
  isJsonParseableField,
  handleAcademicMetricUpdate,
  handleMajorGpaUpdate,
  handleJsonParseableFieldUpdate,
  handleStandardFieldUpdate,
} from './utils/fields.helpers';

export const ProfilePageContainer = () => {
  // redirect the user if they land here on prod
  useRedirectIfNotAuthorized();

  const { profile } = useParams();

  const { isLoading: isLoadingAutoComplete } = useFetchProfileValueOptionsQuery(undefined);
  const { data: userProfile, isLoading: isLoadingUserProfile } =
    useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);
  const [updateProfile] = useUpdateUserProfileMutation();

  if (profile && !isLoadingUserProfile) {
    return <PublicProfile userId={profile} />;
  }

  if (isLoadingAutoComplete || isLoadingUserProfile) {
    return <FullScreenLoading />;
  }

  const handleUpdatingAnswer = async (key: string, value: ProfileValue[]): Promise<void> => {
    try {
      // Handle academic metric fields
      if (isAcademicMetricField(key) && value.length > 0 && isObjectValue(value[0])) {
        await handleAcademicMetricUpdate(updateProfile, value);

        return;
      }

      // Handle major_gpa field specifically
      if (key === 'major_gpa') {
        await handleMajorGpaUpdate(updateProfile, value);

        return;
      }

      // Handle JSON parseable fields
      if (isJsonParseableField(key)) {
        await handleJsonParseableFieldUpdate(updateProfile, key, value);

        return;
      }

      // Handle standard fields
      await handleStandardFieldUpdate(updateProfile, key, value);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  return <ProfilePage handleUpdatingAnswer={handleUpdatingAnswer} userProfile={userProfile} />;
};
